import { Contract, ContractReferenceData } from "../types/contract";

/**
 * Staleness window the contract-reference-data-job uses to pick up stale equities.
 *
 * This mirrors the job's `STALE_DAYS` env var (default 4). No API exposes it today, so
 * this copy drifts silently if the job is retuned -- accepted deliberately (PWA-2586).
 * If plutus-services ever returns a computed eligibility timestamp, read that instead of
 * recomputing here.
 */
export const REFERENCE_DATA_STALE_DAYS = 4;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Only equities are ever selected by the refresh job. */
const REFRESHABLE_SEC_TYPE = "STK";

/**
 * Primary exchange marking a contract untradable. The job matches on `primaryExchange`
 * case-insensitively (not on `untradableSinceUtc`, which was added without a backfill),
 * so this check must do the same.
 */
const UNTRADABLE_PRIMARY_EXCHANGE = "VALUE";

type ReferenceDataSource = Pick<Contract, "referenceData">;

type EligibilitySource = Pick<
  Contract,
  "secType" | "primaryExchange" | "referenceDataReceivedAtUtc"
>;

/**
 * Why a contract will never be picked up by the automatic refresh job. Showing a date
 * for these contracts would promise a refresh that never arrives.
 */
export type RefreshExclusionReason = "NOT_EQUITY" | "UNTRADABLE";

export type RefreshEligibility =
  | { scheduled: true; eligibleAtUtc: string | null }
  | { scheduled: false; reason: RefreshExclusionReason };

export const REFRESH_EXCLUSION_MESSAGES: Record<RefreshExclusionReason, string> = {
  NOT_EQUITY: "Not scheduled -- the refresh job only selects equities (secType STK).",
  UNTRADABLE:
    "Not scheduled -- contracts with primary exchange VALUE are excluded from the refresh queue.",
};

/** The stored reference-data payload, or null when the contract has none. */
export function getReferenceData(contract: ReferenceDataSource): ContractReferenceData | null {
  const referenceData = contract.referenceData;
  if (!referenceData || typeof referenceData !== "object" || Array.isArray(referenceData)) {
    return null;
  }
  return referenceData;
}

/**
 * Split ratio, which exists only inside the referenceData JSON -- there is no typed
 * column for it on `contracts`.
 */
export function getSplitRatio(contract: ReferenceDataSource): string | null {
  const splitRatio = getReferenceData(contract)?.splitRatio;
  if (typeof splitRatio !== "string" || splitRatio.trim() === "") return null;
  return splitRatio;
}

/**
 * The error recorded by the most recent fetch attempt, if it failed. When this is set,
 * the stored values come from an older successful write.
 */
export function getReferenceDataError(contract: ReferenceDataSource): string | null {
  const error = getReferenceData(contract)?.error;
  if (error === null || error === undefined) return null;
  if (typeof error === "string") return error.trim() === "" ? null : error;
  return String(error);
}

/**
 * When the contract next becomes eligible for an automatic refresh.
 *
 * `eligibleAtUtc: null` means eligible now (never fetched, so the job's stale filter
 * already matches it). The job is budget-bound and works a prioritized queue, so this is
 * the earliest it can be picked up, not a promise of when it will be.
 */
export function getRefreshEligibility(contract: EligibilitySource): RefreshEligibility {
  if ((contract.secType || "").trim().toUpperCase() !== REFRESHABLE_SEC_TYPE) {
    return { scheduled: false, reason: "NOT_EQUITY" };
  }

  if ((contract.primaryExchange || "").trim().toUpperCase() === UNTRADABLE_PRIMARY_EXCHANGE) {
    return { scheduled: false, reason: "UNTRADABLE" };
  }

  const receivedAt = contract.referenceDataReceivedAtUtc;
  if (!receivedAt) {
    return { scheduled: true, eligibleAtUtc: null };
  }

  const receivedAtMs = new Date(receivedAt).getTime();
  if (Number.isNaN(receivedAtMs)) {
    return { scheduled: true, eligibleAtUtc: null };
  }

  return {
    scheduled: true,
    eligibleAtUtc: new Date(receivedAtMs + REFERENCE_DATA_STALE_DAYS * MS_PER_DAY).toISOString(),
  };
}
